<script setup lang="ts">
import { EColors, type Colors } from '@/types/constants/ColorValues'
import { computed, ref, type InputHTMLAttributes, type PropType } from 'vue'
import VOtpInput from 'vue3-otp-input'
import SecondaryButton from '../Buttons/SecondaryButton.vue'
import StackLayout from '../Layouts/StackLayout.vue'
import CustomField from './CustomField.vue'

const props = defineProps({
    label: { type: String, required: false },
    type: Object as PropType<Colors>,
    placeholder: { type: String },
    modelValue: String,
    message: String,
    required: Boolean,
    inputType: String as PropType<InputHTMLAttributes['type']>,
    hasError: Boolean,
    errorText: String
})
type Emits = {
    (e: 'change', value?: string): void
    (e: 'complete', value: string): void
}
const emit = defineEmits<Emits>()

const localValue = computed({
    get: () => props.modelValue,
    set: value => emit('change', value)
})

const computedType = computed(() =>
    props.hasError ? EColors.DANGER : props.type ?? EColors.PRIMARY
)

const handleOnChange = (value: string) => (localValue.value = value)

const handleOnComplete = (value: string) => emit('complete', value)
const clearInput = () => {
    localValue.value = undefined
    otpInput.value?.clearInput()
}

const otpInput = ref<{ clearInput: () => void } | null>(null)

defineExpose({ clearInput })
</script>

<template>
    <CustomField
        :label="label ?? ''"
        :message="errorText"
        :type="computedType"
        :required="required"
        class="otp-input-field">
        <StackLayout>
            <StackLayout direction="column" class="otp-layout" :gap="8">
                <v-otp-input
                    ref="otpInput"
                    input-classes="otp-input"
                    separator="-"
                    inputType="letter-numeric"
                    :conditionalClass="['token', 'token', 'token', 'token', 'token', 'token']"
                    :num-inputs="6"
                    :modelValue="localValue"
                    :should-auto-focus="true"
                    :should-focus-order="true"
                    @on-change="handleOnChange"
                    @on-complete="handleOnComplete" />
                <SecondaryButton class="align-self-end" label="Clear input" @click="clearInput()" />
            </StackLayout>
        </StackLayout>
    </CustomField>
</template>

<style scoped lang="scss">
.otp-layout {
    margin: auto;
}
.otp-input-field {
    & :deep(.otp-input) {
        &.token {
            width: 40px;
            height: 60px;
            padding: 5px;
            margin: 0 5px;
            font-size: 20px;
            border-radius: 4px;
            border: 1px solid $primary;
            text-align: center;
            background-color: var(--color-background-secondary);
            color: var(--color-text);

            &:focus-visible {
                outline: none;
                border: 2px solid $primary;
                background-color: var(--color-background);
                color: var(--color-text);
            }
        }
    }
}
/* Background colour of an input field with value */
.otp-input.is-complete {
    background-color: var(--color-background-secondary);
}
.otp-input::-webkit-inner-spin-button,
.otp-input::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
    border: 1px solid $primary;
}
input::placeholder {
    font-size: 15px;
    text-align: center;
    font-weight: 600;
}
</style>
